import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import GenralRates from "./GeneralRates/GeneralRates";
import SpecialRates from "./SpecialRates/SpecialRates";
import DayMapping from "./DayMapping/DayMapping";
import Settings from "./Settings/Settings";
import styles from "./AdminPortal.module.css";
import { Button, Typography } from "@mui/material";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import SportsCricketIcon from "@mui/icons-material/SportsCricket";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import SportsBaseballIcon from "@mui/icons-material/SportsBaseball";
import { getUser } from "../../helpers/sessionStorageHelper";
import { resetBookingSetting } from "../../dux/bookingSetting";

const locationSportsMap = {
  srinagar: ["Football", "Box Cricket", "Cricket Net", "Pickle Ball"],
  // baramulla: ["Pickle Ball"],
  jammu: ["Pickle Ball"],
};

const Tabs = [
  { title: "General Rates", key: "general-rates" },
  { title: "Special Rates", key: "special-rates" },
  { title: "Day Mapping", key: "day-mapping" },
  { title: "Settings", key: "settings" },
];

const sportOptions = [
  { name: "Football", icon: <SportsSoccerIcon /> },
  { name: "Box Cricket", icon: <SportsBaseballIcon /> },
  { name: "Cricket Net", icon: <SportsCricketIcon /> },
  { name: "Pickle Ball", icon: <SportsTennisIcon /> },
];

function getAllowedLocations() {
  const user = getUser();
  return user && user.locations ? user.locations : [];
}

const AdminPortal = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { tab } = useParams(); // Only use tab from params
  const allowedLocations = getAllowedLocations();
  const LOCAL_SELECTED_LOCATION_KEY = "admin_selected_location";
  const LOCAL_SELECTED_SPORT_KEY = "admin_selected_sport";

  const [selectedLocation, setSelectedLocation] = useState(() => {
    try {
      const saved = localStorage.getItem(LOCAL_SELECTED_LOCATION_KEY);
      if (saved && allowedLocations.includes(saved)) {
        return saved;
      }
    } catch {}
    return allowedLocations[0];
  });

  const sportsList = useMemo(
    () => locationSportsMap[selectedLocation?.toLowerCase()] || [],
    [selectedLocation]
  );
  const [selectedSport, setSelectedSport] = useState(() => {
    try {
      const savedSport = localStorage.getItem(LOCAL_SELECTED_SPORT_KEY);
      if (savedSport && (sportsList || []).includes(savedSport)) {
        return savedSport;
      }
    } catch {}
    return sportsList[0];
  });

  // When location changes, keep current sport if still valid; otherwise fallback to first available
  useEffect(() => {
    if (!selectedSport || !sportsList.includes(selectedSport)) {
      if (sportsList.length > 0) {
        setSelectedSport(sportsList[0]);
      }
    }
  }, [selectedLocation, sportsList, selectedSport]);

  // Persist selected location
  useEffect(() => {
    try {
      if (selectedLocation) {
        localStorage.setItem(LOCAL_SELECTED_LOCATION_KEY, selectedLocation);
      }
    } catch {}
  }, [selectedLocation]);

  // Persist selected sport
  useEffect(() => {
    try {
      if (selectedSport) {
        localStorage.setItem(LOCAL_SELECTED_SPORT_KEY, selectedSport);
      }
    } catch {}
  }, [selectedSport]);

  // Reset Redux state when tab changes
  useEffect(() => {
    dispatch(resetBookingSetting());
  }, [tab, dispatch]);

  const changeHandler = (key) => {
    // Reset Redux state when switching tabs
    dispatch(resetBookingSetting());
    navigate(`/admin-portal/${key}`);
  };

  const locationChangeHandler = (e) => {
    const newLoc = e.target.value;
    setSelectedLocation(newLoc);
    // No need to navigate, just update state
  };

  const locationDisplay = {
    srinagar: "Srinagar",
    // baramulla: "Baramulla",
    jammu: "Jammu",
  };

  return (
    <div className={styles.container}>
      <div className={styles.mainWrapper}>
        <div className={styles.sidebar}>
          <div className={styles.tabContainer}>
            {/* ✅ Location Dropdown */}
            <Typography variant="h6" gutterBottom>
              Select Location:
            </Typography>
            <select
              value={selectedLocation}
              onChange={locationChangeHandler}
              className={styles.locationDropdown}
            >
              {allowedLocations.map((loc) => (
                <option key={loc} value={loc}>
                  {locationDisplay[loc] || loc}
                </option>
              ))}
            </select>
            <Typography variant="h6" gutterBottom>
              Select Sport:
            </Typography>
            <div className={styles.availableSport}>
              {sportOptions
                .filter(({ name }) => sportsList.includes(name))
                .map(({ name, icon }) => (
                  <Button
                    key={name}
                    startIcon={icon}
                    onClick={() => setSelectedSport(name)}
                    variant="contained"
                    fullWidth
                    sx={{
                      justifyContent: "flex-start",
                      marginBottom: "8px",
                      backgroundColor:
                        selectedSport === name
                          ? "rgb(253, 199, 11)"
                          : "#09213c",
                      color: selectedSport === name ? "#000" : "#fff",
                      "&:hover": {
                        backgroundColor:
                          selectedSport === name
                            ? "rgb(240, 187, 0)"
                            : "#0c2a4d",
                      },
                    }}
                  >
                    {name}
                  </Button>
                ))}
            </div>
          </div>

          <div className={styles.tabContainer}>
            <Typography variant="h6" gutterBottom>
              Select Type:
            </Typography>
            <div className={styles.rateTabContainer}>
              {Tabs.map(({ title, key }) => (
                <Button
                  key={key}
                  onClick={() => changeHandler(key)}
                  fullWidth
                  sx={{
                    borderRadius: "8px",
                    fontWeight: 500,
                    textTransform: "none",
                    marginBottom: "8px",
                    backgroundColor:
                      key === tab ? "rgb(253, 199, 11)" : "#09213c",
                    color: key === tab ? "#000" : "#fff",
                    "&:hover": {
                      backgroundColor:
                        key === tab ? "rgb(240, 187, 0)" : "#0c2a4d",
                    },
                  }}
                >
                  {title}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.main}>
          {tab === "general-rates" && (
            <GenralRates sport={selectedSport} location={selectedLocation} />
          )}
          {tab === "special-rates" && (
            <SpecialRates sport={selectedSport} location={selectedLocation} />
          )}
          {tab === "day-mapping" && (
            <DayMapping sport={selectedSport} location={selectedLocation} />
          )}
          {tab === "settings" && (
            <Settings selectedLocation={selectedLocation} />
          )}
        </div>
      </div>
      <div className={styles.footer}></div>
    </div>
  );
};

export default AdminPortal;
